import React, { createContext, useContext, useState } from 'react';
import type { AssistantMode, ReportKind, ReportShelfRow, ThreadSummary } from '../../services/assistantService';
import type { QuickKey } from '../assistant/starters';
import type { ReportsMeter, WorksheetSeed } from './analyses/analysesModel';
import type { OutreachCard, OutreachMessage, ProspectRunSummary } from '../../services/dataService';
import type { BearriBadgeName } from '../brand/BearriBadge';
import type { BearriMotionName } from '../brand/motionAssets';
import type { ContextCatalogState, ConversationState } from './useConversation';
import type { DealsPipeline, EmailThread } from './useDealsPipeline';
import type { WhatBearriSees } from './SourcesSheet';
import type { Ask, AskBackChip, AskOrigin, LocalTurn } from './askTypes';
import type { SavedShortlistState } from './scout/useSavedShortlist';

export type { ContextCatalogState } from './useConversation';

/**
 * THE ROOM'S CONTRACTS — the props each piece of the Bearri page (/app/bearri) receives from the
 * frame (pages/Bearri.tsx). A piece is built against these and never edits the page: everything
 * it needs is passed in, and everything it can ask the page to do is on `RoomActions`.
 *
 * THE PAGE IS A CHAT (SPEC2, 2026-09-24): the history rail on the left, the chat as the whole rest of
 * the page. No page head, no drawer sections, no folio tabs.
 *
 *   HistoryRail  the left rail: "+ New chat", then ONE list by day (Today / Yesterday / Previous 7 days /
 *                <Month>) of deal chats, general chats and saved shortlists; its foot says chats are
 *                private. Sticky, scrolls on its own. ≤1023px it is an overlay opened by "Chats".
 *   EmptyChat    a fresh chat (no params, ?new=1): the one pin, "Hi <name>. What are we working on?", one
 *                line, the composer, then the three analyses (tiles) and the four quick questions (slips) (SPEC6).
 *   Thread       a conversation (?deal= / ?thread=): its slim head, the latest real event, the turns,
 *                the running docket (the only pin in a chat while a turn runs), the reply letter.
 *   Composer     the ONE place to ask, pinned at the foot of every chat (not a scout): the field, what
 *                Bearri reads (the foot's one sentence: channels · shortlist · private items · + add), the cost, Ask.
 *   DealPicker   "Work on a deal" / "Pick a deal": every deal grouped by what it needs, Track a thread,
 *                Write a new pitch. Picking opens that deal's chat (a waiting tool and its words come along).
 *   SourcesSheet the LEDGER dialog, "What Bearri reads" (?view=sources): both tabs, the editors.
 *   (?scout=)    new → a new chat with the Find card's words in its composer · <run_id> → that saved shortlist's chat.
 *
 * Shared, read-only state every piece may read: `conv` (useConversation), `pipeline`
 * (useDealsPipeline), `sees` (useWhatBearriSees, exported by SourcesSheet.tsx), `catalog`
 * (useContextCatalog). Quota is read in the piece that prints it, from `useQuota('coach_run')`
 * (answers: useAnswers in Composer.tsx; finding brands is an answer too) — never hard-coded.
 *
 * Layout facts every piece can rely on (room.css):
 *  - The rail is 248px (≥1024). ≤1023px there is no rail beside the chat: a slim bar at the chat's
 *    top holds "Chats" (opens the rail as an overlay) and "New chat". <768 is a phone (16px gutters).
 *  - The chat column (#bearri-work .bb-chat) is centred, at most 860px wide; the page itself scrolls
 *    (never an inner scroller), so sticky heads use top: var(--workspace-bar-h) (58px, 52px on a
 *    phone) and the composer is sticky at bottom: 0. The column is at least the window's height, so
 *    the composer sits at the foot even on an empty chat.
 *  - A sticky surface lying on the desk is painted `background: var(--desk) var(--grain)`.
 *  - z-index: rail 5 · sticky chat head / composer 20 · popovers 30 · the rail overlay and its scrim
 *    45–46 · header 50 · dialogs (Radix: the ledger, the Deal picker) above, scrim over the header too.
 *
 * The composer's unsent words are the frame's too: every view (the fresh chat, each deal, each
 * conversation) keeps its own, so moving between chats never loses what the creator was writing
 * (PadDraftsProvider, below).
 */

/** The page's three views; which one shows comes from the URL. fresh = the empty chat. */
export type RoomView = 'fresh' | 'conversation' | 'scout';

/** A coaching tool = an assistant mode the creator can choose ('auto' and 'general' are Bearri's own routing). */
export type ToolMode = Exclude<AssistantMode, 'auto' | 'general'>;

/** The ledger's two tabs. */
export type SourcesTab = 'conversation' | 'all';

/** A row the ledger should open at (and, for the editors, open): a ledger key or a pasted message `msg-<id>`. */
export type SourcesFocus = 'profile' | 'sponsors' | 'performance' | 'terms' | 'brief' | 'paste' | 'deal' | 'offer'
  | 'email' | 'milestones' | 'memory' | 'gmail' | 'never' | `msg-${string}`;

/** One request to focus a row; `at` changes on every request so asking twice for the same row still fires. */
export interface SourcesFocusRequest { target: SourcesFocus; at: number }

/** The one bear's state: its pose for this context, and whether a job is really queued or running. */
export interface PinState {
  /** writing (default · pitch · rehearse) · mail (reply, replied deals) · sponsors (negotiate, won deals) · channels (deliver) · rewards (renew) · research (scout). Never 'reading'. */
  name: BearriBadgeName;
  /** The motion name BearriBadge takes while busy (it only says "working"). */
  motion: BearriMotionName;
  /** True only while an assistant job or a reply check is actually queued/running. */
  busy: boolean;
}

/** A question handed to the composer by a deal action ("Help me answer", "Draft a follow-up"). Under words the creator
 *  wrote it is added on a new line; it replaces only an empty field or a tool's untouched question. */
export interface PadPrefill { mode: AssistantMode; question: string; includeEmail?: boolean }

/** A draft that can go into the deal's Gmail reply letter (offered only when that letter exists). */
export interface ReplyWith { label: string; onUse: (text: string) => void }

/** Saved scout runs (shortlists), newest first, for the rail and the Scout card. */
export interface ShortlistsState {
  /** Up to 50 saved runs from GET /api/data/prospects/history, newest first. */
  runs: ProspectRunSummary[];
  /** The first read has not finished. */
  loading: boolean;
  /** The last read failed (shown as a line with Retry, never as "none saved"). */
  error: boolean;
  /** Re-read the list (after a scout files, or on Retry). */
  refresh: () => void;
}

/** A scout running now, so the rail and the Scout card can say "Scouting now…": the room's watcher
 *  (retired with the scout view in v3: the page passes null; kept so older pieces still type-check). */
export interface ScoutingState {
  /** The ?scout= target that reopens it (the job id while running). */
  id: string;
  /** The row's second line, e.g. "Filing up to 10 brands". */
  label: string;
}

/** Everything a piece may ask the page to do. Every action is URL/state only: none runs a paid job. */
export interface RoomActions {
  /** Open the ledger, "What Bearri reads" (?view=sources), on a tab, optionally at a row (the brief/paste rows open their editor). */
  openSources: (tab?: SourcesTab, focus?: SourcesFocus) => void;
  /** Close the ledger (focus returns to whatever opened it — the ledger's job). */
  closeSources: () => void;
  /** Set a tool on the composer (null = back to auto). Fills the field with `prefill` ONLY when it is empty (or holds a
   *  tool's untouched words); focuses it. Nothing runs. */
  setTool: (mode: ToolMode | null, prefill?: string) => void;
  /** Put text into the composer: appended on a new line when it already has text, never replacing it. Optional mode. */
  addToPad: (text: string, mode?: AssistantMode) => void;
  /** A deal action's question: fills an empty field (or one holding only a tool's untouched words), and is added on a new
   *  line under the creator's own words — never replacing them. Sets the mode; may switch the deal's email thread on (visibly). */
  prefill: (ask: PadPrefill) => void;
  /** Start a NEW chat (?new=1) with the composer pre-filled — e.g. "Ask Bearri about <brand>". Nothing runs. */
  startConversation: (ask: { mode: AssistantMode; question: string }) => void;
  /** Focus the composer's field (scrolls it into view if needed). */
  focusPad: () => void;
  /** The empty chat (?new=1): clears the composer and the tool. Blocked while an answer is being sent. */
  newConversation: () => void;
  /** Open a deal and its one chat (?deal=). When the composer holds a tool that needs a deal (Deliver, Renew) and none
   *  is open, the tool and its words come with it: "Pick a deal" is one step, not a restart. */
  openDeal: (id: string) => void;
  /** Open a chat that is not about one deal (?thread=). */
  openThread: (id: string) => void;
  /** ?scout=new: a new chat with the Find card's words in the composer (never asked) · ?scout=<run_id>: the chat holding that
   *  saved shortlist (made once, free). `opts` is kept for older callers and ignored: nothing is ever started from here. */
  openScout: (target: 'new' | string, opts?: { start?: boolean }) => void;
  /** The older door, kept for pieces typed against RoomActions: the Deal picker (it carries a tool waiting for a
   *  deal), except from a chat that isn't about a deal, whose way back is the chats list. New pieces call
   *  pickDeal / showChats. */
  showDeals: () => void;
}

/** The chat's actions: RoomActions plus the two doors the chat added. The frame hands every piece this one object;
 *  the pieces built for the chat (rail, empty chat, composer, deal picker) take it, the older ones take RoomActions. */
export interface ChatActions extends RoomActions {
  /** Open the Deal picker dialog ("Work on a deal", "Pick a deal"). While a tool that needs a deal waits on the
   *  composer, the deals it fits are listed first ("Pick for <tool>") and get the focus. */
  pickDeal: () => void;
  /** Show the chats list: the rail's overlay under 1024px; ≥1024 (the rail is always there) its first row takes the focus. */
  showChats: () => void;
}

// ── THE RAIL ─────────────────────────────────────────────────────────────────────────────────

/** HISTORY RAIL (components/bearri/HistoryRail.tsx) — owned by the frame. */
export interface HistoryRailProps {
  /** Every conversation (GET /api/assistant/threads): a deal's has `pitch_id`, a general chat's is null. */
  threads: ThreadSummary[];
  /** The conversation list's first read has not finished (the rail says so; never "no chats"). */
  threadsLoading: boolean;
  /** The conversation list could not be read (a line with Retry). */
  threadsError: boolean;
  onRetryThreads: () => void;
  /** The deals: EVERY deal is a row (v4, SPEC4 C) — its chat's when it has one, else the deal itself (brand print,
   *  brand, one real status line), never twice; a chat whose deal is gone is left out once the deals are read. */
  pipeline: Pick<DealsPipeline, 'cards' | 'loading' | 'error'>;
  shortlists: ShortlistsState;
  /** A scout running now: "Scouting now…" heads Today. */
  scouting: ScoutingState | null;
  /** What is open, so its row is the white slip pulled from the list. */
  selected: { deal: string | null; thread: string | null; scout: string | null; fresh: boolean };
  /** The overlay (≤1023px) is open. ≥1024 the rail is always shown and this is ignored. */
  open: boolean;
  /** Open or close the overlay (the Chats button, Esc, the scrim, ✕, picking a row). Closing returns focus to "Chats". */
  onOpen: (open: boolean) => void;
  /** An answer is being sent: "New chat" waits. */
  sending: boolean;
  room: ChatActions;
  /** v5: the rail's second folder tab, "Reports" — drawn only once the analyses are live (null: the v4 rail, one list). */
  reports?: RailReports | null;
}

/** v5: what the rail's Reports tab lists (GET /api/assistant/reports) and which tab is open. */
export interface RailReports {
  /** The shelf, newest first; null while it has never been read. */
  rows: ReportShelfRow[] | null;
  loading: boolean;
  error: boolean;
  retry: () => void;
  /** The month's reports, for the stub (null: not shown). */
  meter: ReportsMeter | null;
  tab: 'chats' | 'reports';
  onTab: (tab: 'chats' | 'reports') => void;
  /** A report whose chat was deleted was pressed: it opens on its own (ReportSheet). */
  onOpenReport: (row: ReportShelfRow) => void;
}

// ── THE EMPTY CHAT ───────────────────────────────────────────────────────────────────────────

/** EMPTY CHAT (components/bearri/EmptyChat.tsx) — a fresh chat with no turns (no params, ?new=1). */
export interface EmptyChatProps {
  /** The one bear on the empty chat ('writing', ~88px). It rocks only while a job really runs. */
  pin: PinState;
  /** A slip's press — its words go into the composer (the starter kept; under the creator's own words, never over them),
   *  nothing is asked or spent. Returns the words it put there (the slip then hands them over, QuickFlight), or null when
   *  nothing was put there (a question on its way). */
  onUse: (key: QuickKey) => string | null;
  /** The composer, placed between the greeting and the tiles (a phone docks it at the foot from the start). */
  composer?: React.ReactNode;
  /** v6: the Analyses row, right under the box — drawn while the analyses can be live: `meter` is the server's reports
   *  block, or null while the usage summary is still being read (the head then shows no count, never a guess). None at all
   *  once the summary is known to carry no reports (no analysis control before its backend). onOpen opens that worksheet
   *  (nothing runs until its Run); `opener` gets the focus back when the worksheet closes. */
  analyses?: { meter: ReportsMeter | null; onOpen: (kind: ReportKind, opener: HTMLElement | null) => void } | null;
}

/** Opens an analysis's worksheet — never runs one (nothing is read or used until its Run). `seed`: Review an offer opens
 *  with the creator's own pasted words in its field (v6's cross-link under a reply answer). */
export type OpenAnalysis = (kind: ReportKind, opener: HTMLElement | null, seed?: WorksheetSeed) => void;

// ── THE COMPOSER ─────────────────────────────────────────────────────────────────────────────

/**
 * COMPOSER (components/bearri/Composer.tsx) — the ONE place to ask, pinned at the foot of the chat.
 *
 * WHAT IT EDITS (all on `conv`, all sent by Ask, all saved on the turn and restored when a chat reopens):
 *   conv.creatorProfile / setCreatorProfile(p)  CHANNEL chip: the ONE channel read (radio when ≥2 owned, from
 *                                               sees.profiles). Choosing another drops videoIds/sponsorIds.
 *   conv.videoIds / setVideoIds(ids | null)     CHANNEL menu with a catalog: null = "Newest {default_video_count}"
 *                                               (the default) · a list = "Only the ones I pick" (1..12)
 *   conv.sponsorIds / setSponsorIds(ids | null) SPONSORS menu with a catalog: null = "All N on file" — or "Top
 *                                               {default_sponsor_count} on file" when sponsors_total is larger, or
 *                                               "None read yet" when none qualifies · a list (1..20)
 *   conv.includeEmail / setIncludeEmail(on)     GMAIL chip in a deal chat: "Read this deal's email thread when I
 *                                               ask" — OFF in a new chat; forced off by the frame while Gmail can't
 *                                               read. Off drops emailMessageIds.
 *   conv.emailMessageIds / setEmailMessageIds   GMAIL menu when the email is on: null = "All N messages" (a thread
 *                                               of 8 or fewer) / "Newest 8 messages" (a longer one: the engine reads
 *                                               the newest 8) · a list (1..8) of emailMessageId(m) from `emailThread`
 *   conv.selected / toggleSource(id, on)        messages pasted in an older chat (≤6), each OFF until ticked (the
 *                                               ledger manages them; nothing new is pasted outside the question itself)
 *   conv.gmailThreadIds / toggleGmailThread     v4 GMAIL menu ("[M] add an email"): up to 3 of the creator's own Gmail
 *                                               threads (gmail_thread_ids), OFF in a new chat, restored from the last
 *                                               turn; listed by conv.readGmailThreads only while the menu is open
 * A null narrowing is left out of the request (the engine's default). The server rejects []: an empty pick is
 * never offered — "Back to the default" sets null (useConversation also leaves an empty list out). Ask sends a
 * narrowing only once the page can show it (askGate: the catalog read, the thread listed). Backend contract:
 * scratchpad bearri-chat/CONTRACT.md.
 *
 * WHAT IT READS FROM:
 *   `catalog`  the resolved channel's videos and sponsors. `ready` = item pickers; `loading` = the records with
 *              "Checking…"; `unavailable` / `none` = the read-only records only (never fake checkboxes).
 *              The frame prunes a restored narrowing to what the catalog lists, and drops it when the
 *              catalog is unavailable (a narrowing the menu can't show is never sent).
 *   `sees`     the read-only records (profile + audience, sponsors on file, terms, the deal, their offer,
 *              memory), the owned channels, and Gmail's server capability (can_read).
 *   `pipeline` Gmail's connection (connectGmail for Connect / Reconnect in the Gmail menu). The menu's own statuses
 *              and threads come from GET /api/assistant/gmail-threads (conv.readGmailThreads), read only while
 *              it is open — never an "All" email.
 *
 * RULES: Ask (Enter on a keyboard, Shift+Enter a new line; on touch the button; ⌘/Ctrl+Enter always), a quick-start
 * card's tap and a reply chip are the ONLY paid presses: conv.submit(). The field is a textarea
 * labelled "Ask Bearri" (≥16px, auto-grow) and the submit button's accessible name is "Ask Bearri". The cost
 * line comes from useAnswers() (Composer.tsx) and is absent on unlimited plans. The ledger (room.openSources) is reached at
 * ?view=sources and from an answer's "See what I read" — the composer has no door to it (SPEC3).
 * A tool's missing need is one amber line ("Needs their message · Pick from Gmail", "Pick a deal" = room.pickDeal) and
 * Ask stays enabled. Every control changes what the engine reads or is a read-only record: no "coming soon".
 */
export interface ComposerProps {
  /** empty: under the empty chat (the fresh page's words) · chat: at the foot of a conversation. */
  variant: 'empty' | 'chat';
  /** The conversation: the words, the mode, submit, and the whole selection above. */
  conv: ConversationState;
  /** The deal this chat is about ("Ask about Raycon…"), if any. */
  deal: OutreachCard | null;
  /** What the next answer reads: records, owned channels, Gmail's capability. */
  sees: WhatBearriSees;
  /** The resolved channel's catalog (see above). */
  catalog: ContextCatalogState;
  /** Deals, Gmail and its state words; the deal's own email thread is `emailThread`. */
  pipeline: DealsPipeline;
  /** The open deal's Gmail thread (pipeline.emails[deal.id]): messages once loaded, 'loading', 'error', or undefined
   *  when it isn't tracked / hasn't been read. Each message's id for email_message_ids is emailMessageId(m). */
  emailThread: EmailThread | undefined;
  /** The tool set now ("A brand reached out ✕"), or null. */
  tool: ToolMode | null;
  /** How many deals the creator has; null while unknown. 0 → "No deals yet · Write a pitch" instead of "Pick a deal". */
  deals: number | null;
  /** The field: the page focuses it (tools, prefills, "Ask next"). */
  inputRef: React.RefObject<HTMLTextAreaElement>;
  room: ChatActions;
  /** v3: Ask (Enter or the button) goes through the page, which answers a waiting ask-back and adds the CONTRACT3 fields.
   *  Absent: conv.submit(askGate(catalog, emailThread)), exactly as before. */
  onSend?: () => void;
  /** v3: a NEW chat shown as a conversation (Bearri asked back, or a question is on its way) — never a chat that couldn't
   *  be opened, so the composer stays even when opening its first question failed. */
  newChat?: boolean;
}

/** The id a Gmail message is picked by (email_message_ids): the thread endpoint's stable Gmail `id` (not `message_id`,
 *  the RFC header kept for reply threading). null when the server gave none — such a message can't be picked. */
export function emailMessageId(message: OutreachMessage): string | null {
  return typeof message.id === 'string' && message.id ? message.id : null;
}

// ── THE DEAL PICKER ──────────────────────────────────────────────────────────────────────────

/** DEAL PICKER (components/bearri/DealPicker.tsx) — a dialog, mounted only while open. */
export interface DealPickerProps {
  /** Every deal (grouped Needs you / Waiting on them / Drafts / Won / Didn't land by railGroups), the Gmail the list
   *  runs on, and its list-wide actions (Check for replies — a real reply_sync job — and the Track-a-thread inbox). */
  pipeline: DealsPipeline;
  /** The deal open now (its row is marked; a reply check re-reads its email). */
  deal: OutreachCard | null;
  /** A tool on the composer that needs a deal (Deliver, Renew): the deals it fits are listed first under
   *  "Pick for <tool>", and the first of them takes the focus. Picking any deal carries the tool across. */
  pickFor: ToolMode | null;
  /** Close it (Esc, ✕, the scrim, or after a pick). Focus returns to whatever opened it. */
  onClose: () => void;
  /** Open the Track-a-thread dialog (the picker closes first). */
  onTrack: () => void;
  /** openDeal(id) opens the deal's chat (with a waiting tool and its words). "Write a new pitch" goes to Pitch Writer. */
  room: ChatActions;
}

// ── THE CONVERSATION ─────────────────────────────────────────────────────────────────────────

/**
 * THREAD (components/bearri/Thread.tsx) — a conversation, deal or not, on ONE rail (v3, SPEC3 "Conversation"): a slim
 * sticky head ("‹ Chats" ≤1023px, brand print, name, one status line, at most one stage action, •••, the DEMO stamp);
 * the creator's blue notes and Bearri's plain-ink turns; the one bear beside the LATEST Bearri turn only (posed from
 * its real state, rocking only while a job is queued/running), the paw on older ones; one "Based on … · See what I
 * read" line per answer (its own sources dialog); reply chips under the latest answer (they ask through `onAsk`);
 * local turns (ask-backs) after the turn they follow; a saved shortlist as the first Bearri turn of a chat opened
 * from ?scout=<run_id>. "Put in my reply" only via `replyWith`. "Ask with these again" restores a turn's whole
 * selection: source_ids, include_email, creator_profile, then video_ids / sponsor_ids / email_message_ids (set the
 * profile FIRST — choosing a channel drops the narrowing) — offered only when every piece can still be chosen. The
 * composer is NOT part of the thread: the frame renders it after, sticky at the foot.
 */
export interface ThreadProps {
  /** The conversation: thread (turns, older_cursor, brief, shared_messages), loading, active, deleteConversation, saveDraft, loadOlder… */
  conv: ConversationState;
  /** The deal this conversation is about (head, their message, your pitch, the reply letter), or null. */
  deal: OutreachCard | null;
  /** Deal actions (act, won/lost/reopen, busyId), the Gmail thread (emails, loadEmail), the reply (replyDrafts, sendReply). */
  pipeline: DealsPipeline;
  /** What the next answer reads — for "answered with different sources" notes and the ledger jumps. */
  sees: WhatBearriSees;
  /** The resolved channel's catalog: "Ask with these again" is offered for picked videos and sponsors only while it
   *  still lists every one of them. */
  catalog: ContextCatalogState;
  /** The page's pose for the one bear (the chat's latest Bearri turn carries it; its pose follows that turn's real
   *  state, this is the fallback while a question is only being sent). */
  pin: PinState;
  /** "Put in my reply to <brand>" — present only when the deal is Gmail-tracked AND its thread has loaded (so the letter exists). */
  replyWith?: ReplyWith;
  /** The reply letter's textarea: replyWith.onUse fills the draft, then the page focuses this. */
  replyRef: React.RefObject<HTMLTextAreaElement>;
  /** Whether the deal's email is shown open (defaults open once they have replied). */
  emailOpen: boolean;
  /** Open or fold the deal's email. */
  onEmailOpen: (open: boolean) => void;
  /** For a deal with no answers yet: its next moves, one compact row of tool chips under the deal's opening event
   *  (the frame renders EmptyChat's NextMoves here). Shown only until the first answer. */
  nextMoves?: React.ReactNode;
  room: RoomActions;
  /** v3 (COMPOSE): local, free turns — the creator's tapped prompt, Bearri's ask-backs — each shown after the server
   *  turn named by afterTurnId (null = before every server turn). */
  localTurns?: LocalTurn[];
  /** Where each sent question came from, by its exact words (a card, a chip, a shortlist row), for "from a quick start". */
  sentOrigins?: ReadonlyMap<string, AskOrigin>;
  /** A new chat not saved yet (only free ask-backs, or its first question on the way): no "couldn't be opened", named "New chat". */
  fresh?: boolean;
  /** v3: ask in this chat (reply chips, the shortlist's rows). Absent: the words go to the composer (room.addToPad). */
  onAsk?: Ask;
  /** v3: an ask-back chip was tapped. */
  onChip?: (chip: AskBackChip) => void;
  /** The demo account (is_demo): the DEMO stamp in the chat head. */
  isDemo?: boolean;
  /** A saved scout run this chat is attached to (?scout=<run_id>), read for its first, local Bearri turn. */
  savedRun?: SavedShortlistState;
  /** v5: the analyses, when live — a free answer that is a report's job offers its worksheet ("Open Find brands"); v6: a
   *  reply or negotiate answer links to Review an offer. */
  analyses?: { meter: ReportsMeter; open: OpenAnalysis } | null;
  /** v5: this month's chat is used up — the day it comes back ('' when unknown); the reply chips wait (they'd spend chat). */
  chatOut?: string | null;
}

/** SOURCES SHEET (components/bearri/SourcesSheet.tsx) — the LEDGER dialog, "What Bearri reads". */
export interface SourcesSheetProps {
  /** The ledger is open (?view=sources, an answer's "See what I read" → "Everything Bearri can read", a Gmail fix). */
  open: boolean;
  /** Which tab: This conversation | All access. */
  tab: SourcesTab;
  /** A row to open at (the brief/paste/delivery rows open their editor); null = the top. */
  focus: SourcesFocusRequest | null;
  /** Switch tab. */
  onTab: (tab: SourcesTab) => void;
  /** Close it (Esc, ✕, the scrim). */
  onClose: () => void;
  /** The conversation whose sources these are (selected, includeEmail, creatorProfile, thread, saveContext, ensureThread). */
  conv: ConversationState;
  /** The deal, when this conversation is about one. */
  deal: OutreachCard | null;
  /** Gmail state + connectGmail / recheckGmail (reason codes: theirs to fix vs on our side). */
  pipeline: DealsPipeline;
  /** The ledger's data. */
  sees: WhatBearriSees;
  /** The resolved channel's catalog: with it, a channel that has no saved videos is never said to have recent ones. */
  catalog?: ContextCatalogState;
  room: RoomActions;
}

/* SCOUT VIEW is retired (SPEC3): finding brands is a chat turn, and its shortlist a sheet in the chat
   (scout/ShortlistSheet.tsx). ?scout=new / ?scout=<run_id> only lead into a chat (pages/Bearri.tsx). */

// ── A DEAL'S NEXT MOVES ──────────────────────────────────────────────────────────────────────

/** NEXT MOVES (EmptyChat.tsx `NextMoves`) — a deal's chat before its first answer: one compact row of the tools its
 *  stage calls for (each fills the composer; nothing runs), the rest one press further ("More tools"). */
export interface NextMovesProps {
  deal: OutreachCard | null;
  /** The tool on the composer now (its chip is pressed; a second press takes it off). */
  tool: ToolMode | null;
  /** An answer is being sent: the chips wait. */
  busy: boolean;
  /** What each tool works from for this deal (the chip's description): the channel, the offer read, the delivery record… */
  sees: WhatBearriSees;
  room: RoomActions;
}

// ── THE PAD'S UNSENT WORDS ───────────────────────────────────────────────────────────────────

/** What one view's composer holds while the creator is elsewhere: the words, the tool (mode), and whether the words are
 *  still only a tool's or a deal action's untouched question (so a later tool may replace them). */
export interface PadDraft { question: string; mode: AssistantMode; prefilled: boolean }

const PadDraftsContext = createContext<Map<string, PadDraft> | null>(null);

/**
 * The pad's unsent words for each view — `fresh`, `deal:<id>`, `thread:<id>` — kept in memory for the signed-in
 * workspace (MainLayout mounts it inside the account-keyed provider, so a sign-out or an account switch drops it),
 * and never written to browser storage: a question can hold a brand's private terms. Leaving the Bearri page and
 * coming back keeps them; a reload does not.
 */
export function PadDraftsProvider({ children }: { children: React.ReactNode }) {
  const [drafts] = useState(() => new Map<string, PadDraft>());
  return React.createElement(PadDraftsContext.Provider, { value: drafts }, children);
}

/** The workspace's drafts, or (outside MainLayout: a design preview, a test) this page's own, for as long as it is up. */
export function usePadDrafts(): Map<string, PadDraft> {
  const shared = useContext(PadDraftsContext);
  const [own] = useState(() => new Map<string, PadDraft>());
  return shared || own;
}
